/**
 * Immigration Operations Q&A cluster (TM.30/TM.47, extensions, re-entry,
 * work permit, PR/naturalization, overstay). Deterministic generator.
 * No service prices — official fees only when published by the agency.
 */
import { IMMIGRATION_SERVICES, IMMIGRATION_NATIONALITIES } from "./index";
import { liftFlatToFloor } from "../_qa/topup-merge";
import { IMMIGRATION_TOPUP_AXIS } from "../_qa/topup-axes-2";

export type ImmigrationQA = {
  slug: string;
  q: string;
  a: string;
  synonyms: string[];
  serviceSlug?: string;
};

export const IMMIGRATION_REVIEWED_TH = "สิงหาคม 2569";
export const IMMIGRATION_REVIEWED_ISO = "2026-08-06T00:00:00.000Z";

const ASOF = `ข้อมูลทบทวน ณ ${IMMIGRATION_REVIEWED_TH} หลักเกณฑ์ เอกสาร และช่องทางยื่นอาจเปลี่ยนตามประกาศของสำนักงานตรวจคนเข้าเมือง กรมการจัดหางาน BOI หรือกระทรวงมหาดไทย ควรตรวจประกาศล่าสุดก่อนยื่น เว็บไซต์ไม่แสดงค่าบริการ กรุณาสอบถามทางโทรศัพท์ LINE หรืออีเมล`;

const CORE: ImmigrationQA[] = [
  {
    slug: "immigration-tm30-who-reports",
    q: "ใครมีหน้าที่แจ้งที่พักคนต่างชาติ TM.30 และต้องแจ้งเมื่อใด",
    a: `หน้าที่แจ้งเป็นของเจ้าบ้าน เจ้าของเคหสถาน ผู้ครอบครอง หรือผู้จัดการโรงแรมหรืออาคารที่คนต่างชาติเข้าพัก ไม่ใช่หน้าที่ของคนต่างชาติเอง ต้องแจ้งภายในกำหนดเวลาที่กฎหมายกำหนดนับจากวันที่เข้าพัก และแจ้งใหม่เมื่อมีการเปลี่ยนที่พักหรือกลับเข้าประเทศตามแนวปฏิบัติของพื้นที่นั้น อ้างอิงพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 มาตรา 38 และประกาศของสำนักงานตรวจคนเข้าเมือง ${ASOF}`,
    synonyms: ["TM30 ใครแจ้ง", "TM30 who must notify Thailand"],
  },
  {
    slug: "immigration-tm47-window",
    q: "รายงานตัว 90 วัน TM.47 ยื่นก่อนหรือหลังกำหนดได้กี่วัน",
    a: `แนวปฏิบัติทั่วไปคือยื่นได้ล่วงหน้าก่อนวันครบกำหนดตามช่วงที่ตรวจคนเข้าเมืองกำหนด และมีช่วงผ่อนผันสั้น ๆ หลังวันครบกำหนดซึ่งอาจมีค่าปรับ ช่วงเวลาที่รับยื่นและช่องทางออนไลน์อาจแตกต่างกันตามสำนักงานพื้นที่ ควรตรวจกำหนดวันจากใบรับครั้งล่าสุดและยื่นล่วงหน้าเพื่อลดความเสี่ยง ${ASOF}`,
    synonyms: ["รายงานตัว 90 วัน ล่วงหน้ากี่วัน", "90 day report window Thailand"],
  },
  {
    slug: "immigration-reentry-permit-need",
    q: "ต้องขอ Re-entry Permit ก่อนออกนอกประเทศทุกครั้งหรือไม่",
    a: `ผู้ที่ถือการอนุญาตให้อยู่ต่อแบบ single entry จะสูญเสียสิทธิอยู่ต่อเมื่อเดินทางออกโดยไม่มี Re-entry Permit จึงต้องขอก่อนออกทุกครั้ง ผู้ถือวีซ่าหรือสิทธิพำนักบางประเภทที่รองรับการเข้าออกหลายครั้งอาจไม่ต้องขอ ให้ตรวจตราประทับในหนังสือเดินทางและเงื่อนไขวีซ่าของตนเองก่อนเดินทางเสมอ ${ASOF}`,
    synonyms: ["Re-entry permit จำเป็นไหม", "re-entry permit single entry extension"],
  },
  {
    slug: "immigration-overstay-consequences",
    q: "อยู่เกินกำหนด overstay มีผลอย่างไรและควรทำอย่างไร",
    a: `การอยู่เกินกำหนดมีทั้งค่าปรับตามจำนวนวันและผลห้ามเข้าประเทศตามระยะเวลาที่กฎหมายกำหนด โดยผลจะรุนแรงขึ้นตามจำนวนวันและกรณีที่ถูกจับกุม แนวทางที่ควรทำคือหยุดสะสมวันทันที ปรึกษาทนายหรือเจ้าหน้าที่เพื่อประเมินทางเลือกในการเดินทางออกหรือแก้สถานะ และเก็บหลักฐานเหตุจำเป็น เช่น เอกสารทางการแพทย์ ไว้ประกอบการพิจารณา ${ASOF}`,
    synonyms: ["overstay ค่าปรับ blacklist", "overstay penalty Thailand what to do"],
  },
  {
    slug: "immigration-work-permit-vs-visa",
    q: "วีซ่าทำงานกับใบอนุญาตทำงานต่างกันอย่างไร",
    a: `วีซ่าหรือการอนุญาตให้อยู่ต่อเป็นเรื่องสิทธิพำนักที่พิจารณาโดยตรวจคนเข้าเมือง ส่วนใบอนุญาตทำงานเป็นเรื่องสิทธิทำงานที่พิจารณาโดยกรมการจัดหางาน การมีวีซ่าไม่ได้ทำให้ทำงานได้เอง และการมีใบอนุญาตทำงานก็ไม่ต่ออายุสิทธิพำนักให้อัตโนมัติ ต้องดูแลทั้งสองเรื่องพร้อมกันและให้ข้อมูลตำแหน่ง สถานที่ทำงาน และนายจ้างสอดคล้องกันทุกฉบับ ${ASOF}`,
    synonyms: ["วีซ่า Non-B กับ work permit", "visa vs work permit Thailand"],
  },
  {
    slug: "immigration-documents-legalisation",
    q: "เอกสารต่างประเทศที่ใช้ยื่นงานตรวจคนเข้าเมืองต้องรับรองอย่างไร",
    a: `ต้องมีคำแปลที่ถูกต้องและผ่านการรับรองตามที่หน่วยงานผู้พิจารณากำหนด เอกสารหลายประเภทต้องผ่านการรับรองจากหน่วยงานต้นทางและกรมการกงสุลก่อน ค่าธรรมเนียมนิติกรณ์ที่กรมการกงสุลประกาศคือ 200 บาทต่อตราประทับสำหรับบริการปกติ และ 400 บาทต่อตราประทับสำหรับบริการด่วน เมื่ออนุสัญญา Apostille มีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 เอกสารระหว่างประเทศภาคีอาจใช้ Apostille แทนการรับรองหลายชั้นได้ ${ASOF}`,
    synonyms: ["รับรองเอกสารยื่น ตม.", "legalisation documents immigration Thailand"],
  },
];

function serviceQA(s: (typeof IMMIGRATION_SERVICES)[number]): ImmigrationQA[] {
  const rows: Array<[string, string, string]> = [
    ["what", `${s.nameTh} คืออะไรและครอบคลุมงานใด`, `${s.nameTh} (${s.nameEn}) ครอบคลุมงานหลักคือ ${s.keyDeliverables.slice(0, 3).join(" · ")} เหมาะกับกรณี ${s.bestFor.slice(0, 2).join(" · ")} ฐานกฎหมายที่เกี่ยวข้องคือ ${s.legalBasis} ${ASOF}`],
    ["documents", `${s.nameTh} ต้องเตรียมเอกสารอะไร`, `เอกสารตั้งต้นได้แก่ ${s.requiredDocs.join(" · ")} เอกสารภาษาต่างประเทศต้องมีคำแปลและการรับรองตามที่หน่วยงานกำหนด และควรเตรียมสำเนาที่อ่านชัดครบทุกหน้าพร้อมต้นฉบับให้เจ้าหน้าที่ตรวจ ${ASOF}`],
    ["steps", `${s.nameTh} มีขั้นตอนอย่างไร`, `เริ่มจากตรวจสถานะและกำหนดวันที่เกี่ยวข้อง จัดเตรียมเอกสารตามรายการ ยื่นผ่านช่องทางที่หน่วยงานกำหนด ตอบข้อสอบถามหรือส่งเอกสารเพิ่มเติมหากถูกเรียก แล้วเก็บใบรับและหลักฐานทุกฉบับไว้อ้างอิงในรอบถัดไป กรอบเวลาอ้างอิงประมาณ ${s.turnaroundDays} วันทำการเมื่อเอกสารครบ ${ASOF}`],
    ["deadline", `${s.nameTh} มีกำหนดเวลาที่พลาดไม่ได้อะไร`, `ต้องเฝ้าระวังวันหมดอายุการอนุญาตให้อยู่ต่อ วันครบกำหนดรายงานตัว วันสิ้นสุดสัญญาจ้างหรือสัญญาเช่า และวันหมดอายุเอกสารประกอบ เพราะเมื่อพลาดกำหนดแล้วอาจต้องเริ่มกระบวนการใหม่หรือมีค่าปรับ ควรตั้งเตือนล่วงหน้าอย่างน้อยหนึ่งเดือนสำหรับงานที่ต้องขอเอกสารจากต่างประเทศ ${ASOF}`],
    ["risks", `${s.nameTh} มีข้อควรระวังอะไร`, `ข้อควรระวังคือข้อมูลที่ให้ไว้ไม่สอดคล้องกันระหว่างแบบคำขอ สัญญา และเอกสารที่พัก การยื่นล่าช้าจนเข้าเงื่อนไขค่าปรับ และการเข้าใจว่าใบรับจากระบบออนไลน์เท่ากับการอนุมัติ ให้ตรวจสถานะจนได้ผลยืนยันและยึด ${s.legalBasis} เป็นกรอบ ${ASOF}`],
    ["quote", `${s.nameTh} คิดค่าบริการอย่างไร`, `ค่าบริการประเมินเป็นรายเคสตามจำนวนผู้ยื่น จำนวนแบบและรอบที่ต้องยื่น งานย้อนหลังที่ต้องแก้ และงานแปลหรือรับรองเอกสาร เว็บไซต์จึงไม่แสดงราคาเหมารวม ค่าธรรมเนียมราชการเป็นไปตามอัตราที่หน่วยงานประกาศและชำระตรงต่อหน่วยงาน สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล ${ASOF}`],
  ];
  return rows.map(([suffix, q, a]) => ({
    slug: `${s.slug}-${suffix}`,
    q,
    a,
    synonyms: [`${s.nameTh} ${suffix}`, `${s.nameEn.toLowerCase()} ${suffix}`],
    serviceSlug: s.slug,
  }));
}

function matrixQA(): ImmigrationQA[] {
  const out: ImmigrationQA[] = [];
  for (const s of IMMIGRATION_SERVICES) {
    for (const n of IMMIGRATION_NATIONALITIES) {
      out.push({
        slug: `${s.slug}-${n.slug}-docs`,
        q: `ผู้ถือสัญชาติ${n.nameTh}ทำ ${s.nameTh} ต้องเตรียมเอกสารอะไรเพิ่ม`,
        a: `สำหรับผู้ถือสัญชาติ${n.nameTh} (${n.nameEn}) ประเด็นเฉพาะที่ต้องตรวจคือ ${n.notes} นอกจากชุดเอกสารมาตรฐาน ได้แก่ ${s.requiredDocs.join(" · ")} จึงควรยืนยันรูปแบบเอกสารที่หน่วยงานต้นทางออกให้ได้จริงก่อนวางแผนยื่น ยึด ${s.legalBasis} เป็นกรอบพิจารณา ${ASOF}`,
        synonyms: [`${s.nameTh} ${n.nameTh}`, `${s.nameEn} ${n.nameEn} documents`],
        serviceSlug: s.slug,
      });
      out.push({
        slug: `${s.slug}-${n.slug}-process`,
        q: `ผู้ถือสัญชาติ${n.nameTh}ควรวางลำดับ ${s.nameTh} อย่างไร`,
        a: `ลำดับที่ลดความเสี่ยงที่สุดคือขอเอกสารต้นทางของ${n.nameTh}ก่อน เพราะใช้เวลานานและมีข้อจำกัดตามที่ระบุว่า ${n.notes} จากนั้นแปลและรับรองเอกสารให้ครบชั้น แล้วจึงยื่นคำขอโดยเผื่อเวลาประมาณ ${s.turnaroundDays} วันทำการหลังเอกสารครบ พร้อมเก็บใบรับทุกขั้นตอน ${ASOF}`,
        synonyms: [`${s.nameTh} ขั้นตอน ${n.nameTh}`, `${s.nameEn} process ${n.nameEn}`],
        serviceSlug: s.slug,
      });
    }
  }
  return out;
}

export const IMMIGRATION_QA_ALL: ImmigrationQA[] = (() => {
  const all = [...CORE, ...IMMIGRATION_SERVICES.flatMap(serviceQA), ...matrixQA()];
  const seen = new Set<string>();
  return all.filter((x) => (seen.has(x.slug) ? false : (seen.add(x.slug), true)));
})();

liftFlatToFloor(IMMIGRATION_QA_ALL, {
  prefix: "immigration-t7",
  asOf: ASOF,
  authorityTh: "สำนักงานตรวจคนเข้าเมืองและหน่วยงานที่เกี่ยวข้อง",
  clusterTh: "งานตรวจคนเข้าเมืองและการอยู่ต่อในไทย",
  primary: IMMIGRATION_TOPUP_AXIS,
});

export const IMMIGRATION_QA_BY_SLUG: Record<string, ImmigrationQA> = Object.fromEntries(
  IMMIGRATION_QA_ALL.map((x) => [x.slug, x]),
);

export const IMMIGRATION_QA_PHRASING_COUNT = IMMIGRATION_QA_ALL.reduce(
  (n, qa) => n + 1 + qa.synonyms.length,
  0,
);
